import {
  createContext,
  FC,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { useRecoilValue } from "recoil";

import { usePageContext } from "@ryde/redux";
import { tableSearchAF } from "@ryde/shared";

import { decorateGridValueWithSearch } from "../../../../utils/decorate-grid-value.helpers";

export interface ISearchHighlightContextValue {
  decorateValue: (value: ReactNode) => ReactNode;
}

const identityDecorateValue = (value: ReactNode): ReactNode => value;

const defaultSearchHighlightContextValue: ISearchHighlightContextValue = {
  decorateValue: identityDecorateValue,
};

export const SearchHighlightContext =
  createContext<ISearchHighlightContextValue>(
    defaultSearchHighlightContextValue,
  );

export const SearchHighlightProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const { entity } = usePageContext();
  const searchValue = useRecoilValue(tableSearchAF(entity));

  const decorateValue = useCallback(
    (value: ReactNode): ReactNode =>
      decorateGridValueWithSearch(value, searchValue),
    [searchValue],
  );

  const contextValue = useMemo(
    (): ISearchHighlightContextValue => ({ decorateValue }),
    [decorateValue],
  );

  return (
    <SearchHighlightContext.Provider value={contextValue}>
      {children}
    </SearchHighlightContext.Provider>
  );
};

/** Grid cells: single search subscription via provider, not per cell. */
export const useSearchHighlightDecorator = (): ISearchHighlightContextValue =>
  useContext(SearchHighlightContext);
